import { useState } from "react";
import type { Client, FixedFee, ClientType, FeeFrequency } from "@ascente/shared-types";

interface Props {
  client: Client;
  onSave: (client: Client) => void;
  onClose: () => void;
  onArchive: () => void;
}

const CLIENT_TYPES: { value: ClientType; label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "hourly_fixed", label: "Hourly + Fixed" },
  { value: "per_task_fixed", label: "Per Task + Fixed" },
  { value: "fixed_quarterly", label: "Fixed Quarterly" },
];

const FEE_FREQUENCIES: { value: FeeFrequency; label: string }[] = [
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "some_months", label: "Some Months" },
  { value: "per_occurrence", label: "Per Occurrence (not billed — log as an entry)" },
];

export function EditClientModal({ client, onSave, onClose, onArchive }: Props) {
  const [name, setName] = useState(client.name);
  const [type, setType] = useState<ClientType>(client.type);
  const [rate, setRate] = useState(String(client.rate));
  const [email, setEmail] = useState(client.email ?? "");
  const [fixedFees, setFixedFees] = useState<FixedFee[]>(client.fixedFees);

  const updateFee = (i: number, patch: Partial<FixedFee>) => {
    setFixedFees((prev) => prev.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
  };

  const addFee = () => setFixedFees((prev) => [...prev, { name: "", amount: 0, frequency: "monthly" }]);
  const removeFee = (i: number) => setFixedFees((prev) => prev.filter((_, idx) => idx !== i));

  const handleSave = () => {
    onSave({ ...client, name, type, rate: parseFloat(rate) || 0, email, fixedFees });
  };

  const showRate = type === "hourly" || type === "hourly_fixed";

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <h3 className="modal-title">Edit Client</h3>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label className="inp-label">Client Name</label>
            <input className="inp" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="form-group">
            <label className="inp-label">Email</label>
            <input className="inp" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="inp-label">Billing Type</label>
            <select className="sel" value={type} onChange={(e) => setType(e.target.value as ClientType)}>
              {CLIENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          {showRate && (
            <div className="form-group">
              <label className="inp-label">Hourly Rate ($)</label>
              <input type="number" className="inp" value={rate} min="0" step="0.01" onChange={(e) => setRate(e.target.value)} />
            </div>
          )}
        </div>

        {/* Fixed Fees */}
        <div style={{ marginTop: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <label className="inp-label" style={{ marginBottom: 0 }}>Fixed Fees</label>
            <button className="btn btn-secondary btn-sm" onClick={addFee}>+ Add Fee</button>
          </div>
          {fixedFees.map((fee, i) => (
            <div key={i} className="fee-row">
              <input className="inp" placeholder="Name" value={fee.name} onChange={(e) => updateFee(i, { name: e.target.value })} style={{ flex: 2 }} />
              <input type="number" className="inp" placeholder="Amount" value={fee.amount} min="0" step="0.01" onChange={(e) => updateFee(i, { amount: parseFloat(e.target.value) || 0 })} style={{ width: 100 }} />
              <select className="sel" value={fee.frequency} onChange={(e) => updateFee(i, { frequency: e.target.value as FeeFrequency })}>
                {FEE_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
              </select>
              <button className="btn btn-danger btn-sm" onClick={() => removeFee(i)}>✕</button>
            </div>
          ))}
        </div>

        <div className="modal-actions">
          <button
            className="btn btn-secondary"
            onClick={() => { if (confirm(`Archive ${name}?`)) onArchive(); }}
          >
            Archive
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    </div>
  );
}
